/** Split "SiteA + SiteB - Ad Format" into site tokens (before the format suffix). */
import { LeadOpsMatch } from '../../../api/leadOps';
import { daysSince } from '../../../utils/format';
import { AD_FORMAT_REFERENCE } from './adFormats';

const QUALIFIED_VALUES = new Set(['qualified', 'pre-qualified']);
const CONTACTED_VALUES = new Set(['contacted']);
const NEW_VALUES = new Set(['new']);

export const isLeadsBoard = (match: Pick<LeadOpsMatch, 'board_id' | 'board_name'>, leadsBoardId = '') => {
  if (leadsBoardId && match.board_id && String(match.board_id).trim() === leadsBoardId) return true;
  return (match.board_name || '').trim().toLowerCase() === 'leads';
};

export const statusMatches = (match: LeadOpsMatch | null | undefined, values: Set<string>) => {
  const groupName = (match?.group_name || '').toLowerCase().trim();
  const leadStatus = (match?.lead_status || '').toLowerCase().trim();
  return values.has(groupName) || values.has(leadStatus);
};

export const isMatchQualified = (match: LeadOpsMatch | null | undefined) =>
  statusMatches(match, QUALIFIED_VALUES);

export const isMatchContacted = (match: LeadOpsMatch | null | undefined) =>
  statusMatches(match, CONTACTED_VALUES);

export const isMatchNew = (match: LeadOpsMatch | null | undefined) =>
  statusMatches(match, NEW_VALUES);

export const primaryRank = (match: LeadOpsMatch, leadsBoardId: string, inactivityThreshold: number) => {
  if (!isLeadsBoard(match, leadsBoardId)) return [3, 1, 1, 9999, 9999];
  const status = isMatchQualified(match) ? 0 : isMatchContacted(match) ? 1 : isMatchNew(match) ? 2 : 2;
  const hasManager = (match.owner_name || '').trim() ? 0 : 1;
  const inactiveDays = daysSince(match.last_activity_at);
  const isStale = inactiveDays != null && inactiveDays >= inactivityThreshold ? 1 : 0;
  const freshness = inactiveDays != null ? inactiveDays : 9999;
  return [0, status, hasManager, isStale, freshness];
};

export const getDomainRoot = (domain: string) => {
  if (!domain || typeof domain !== 'string') return '';
  const parts = domain.toLowerCase().trim().split('.');
  if (parts.length >= 2) return parts[parts.length - 2];
  return parts[0] || '';
};

export const domainsAreRelated = (a: string, b: string) => {
  if (!a || !b) return false;
  const x = a.toLowerCase().trim();
  const y = b.toLowerCase().trim();
  if (x === y) return true;
  if (x.endsWith('.' + y) || y.endsWith('.' + x)) return true;
  return false;
};

export const isExactDomainMatch = (match: LeadOpsMatch, domainCanonical: string) =>
  Boolean(
    domainCanonical &&
    (match.domain_canonical === domainCanonical || (match.domains || []).includes(domainCanonical))
  );

export const isRelatedDomain = (match: LeadOpsMatch, domainCanonical: string) => {
  if (!domainCanonical) return false;
  const matchDomains = [match.domain_canonical, ...(match.domains || [])].filter(Boolean);
  const search = domainCanonical.toLowerCase();
  for (const matchDomain of matchDomains) {
    const domain = String(matchDomain).toLowerCase();
    if (domain === search) return true;
    if (domain.endsWith('.' + search) || search.endsWith('.' + domain)) return true;
  }
  return false;
};

export const resolveActivityReassign = (
  canReassign: boolean | null | undefined,
  reason: string | null | undefined,
  qualifiedPartnerFound: boolean,
) => ({
  can_reassign: qualifiedPartnerFound ? false : Boolean(canReassign),
  reassign_reason: qualifiedPartnerFound
    ? 'Qualified partner — reassignment is blocked'
    : reason ?? null,
});

/** Extra Monday title suffixes that are not always in AD_FORMAT_REFERENCE. */
const EXTRA_FORMAT_SUFFIXES = [
  'Banner header',
  'Banner',
  'menu button',
  'Natives',
] as const;

const formatSuffixPattern = (() => {
  const labels = [...AD_FORMAT_REFERENCE, ...EXTRA_FORMAT_SUFFIXES]
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  // Allow optional spaces around the hyphen: "pornDD- Banner header" or "pornDD - Top bar"
  return new RegExp(`\\s*-\\s*(?:${labels.join('|')})\\s*$`, 'i');
})();

const FORMAT_NOISE_RE = /banner|header|native|popunder|pre-?roll|slider|widget|interstitial|forum|instant/i;

/** Split "SiteA + SiteB - Ad Format" into site tokens (format suffix stripped per part). */
export const splitInventorySiteTokens = (name: string): string[] => {
  const raw = String(name || '').trim();
  if (!raw) return [];
  return raw
    .split(/\s*(?:\+|\/|&|,|\band\b)\s*/i)
    .map((part) => part.replace(formatSuffixPattern, '').trim())
    .filter(Boolean);
};

const looksLikeDomain = (value: string): boolean =>
  /^(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i.test(value.trim());

const normalizePitchDomainCandidate = (value: string): string => {
  let cleaned = value.trim().toLowerCase();
  cleaned = cleaned.replace(/^https?:\/\//, '').replace(/^www\./, '');
  cleaned = cleaned.replace(/\/.*$/, '').trim();
  if (!cleaned) return '';
  if (looksLikeDomain(cleaned)) return cleaned;
  // Bare brand in inventory titles (e.g. "Porntn") → try .com for Similarweb.
  const brand = cleaned.replace(/\s+/g, '');
  if (!/^[a-z0-9-]+$/i.test(brand)) return '';
  // Reject leftover format residue like "porndd-bannerheader"
  if (FORMAT_NOISE_RE.test(brand)) return '';
  return `${brand}.com`;
};

/**
 * Candidate domains for inventory pitch Similarweb metrics.
 * Prefers Monday-extracted domains / linked websites; if the card title lists
 * multiple brands without TLDs (A + B - Format), expands them into picker options.
 */
export const resolveInventoryPitchDomains = (
  match: Pick<LeadOpsMatch, 'name' | 'domain' | 'domain_canonical' | 'domains' | 'linked_websites'> | null | undefined,
  fallbackDomain = '',
): string[] => {
  const seen = new Set<string>();
  const domains: string[] = [];
  const add = (raw: string) => {
    const value = normalizePitchDomainCandidate(raw);
    if (!value || seen.has(value)) return;
    seen.add(value);
    domains.push(value);
  };

  for (const item of match?.domains || []) add(String(item || ''));
  if (match?.domain_canonical) add(String(match.domain_canonical));
  for (const website of match?.linked_websites || []) {
    for (const item of website.domains || []) add(String(item || ''));
    if (website.name) add(String(website.name));
  }

  const crmCount = domains.length;
  const nameTokens = splitInventorySiteTokens(String(match?.name || ''));
  // Title brands only when CRM did not already give multiple real domains.
  if (nameTokens.length > 1 && crmCount < 2) {
    nameTokens.forEach(add);
  }

  if (domains.length > 0) return domains;

  if (match?.domain) add(String(match.domain));
  if (fallbackDomain) add(fallbackDomain);
  return domains;
};
